import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import i18n from '../i18n';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // 更新 state 以顯示錯誤 UI
    console.error('🚨 [ErrorBoundary] Error caught by getDerivedStateFromError:', error);
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🚨 [ErrorBoundary] Error caught by componentDidCatch:', error);
    console.error('🚨 [ErrorBoundary] Error info:', errorInfo);
    console.error('🚨 [ErrorBoundary] Component stack:', errorInfo.componentStack);
    
    // 將錯誤資訊輸出到瀏覽器控制台，方便調試
    console.group('🚨 Error Boundary - Detailed Error Information');
    console.error('Error:', error);
    console.error('Error Message:', error.message);
    console.error('Error Stack:', error.stack);
    console.error('Component Stack:', errorInfo.componentStack);
    console.groupEnd();
    
    this.setState({
      error,
      errorInfo
    });
  }

  private handleRetry = () => {
    console.log('🔄 [ErrorBoundary] User clicked retry, resetting error state');
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  private handleReload = () => {
    console.log('🔄 [ErrorBoundary] User clicked reload, reloading page');
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      // 獲取翻譯函數 (在 class component 中需要直接使用 i18n)
      const t = (key: string, fallback?: string) => {
        return i18n.t(key, fallback);
      };
      
      // 錯誤 UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-100 via-orange-50 to-yellow-100 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl border-4 border-red-500 shadow-2xl p-8 max-w-md w-full text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            
            <h1 className="text-2xl font-black text-red-600 mb-4">{t('error.appError')}</h1>
            <p className="text-gray-700 font-semibold mb-6">
              {t('error.unexpectedError')}
            </p>
            
            {/* 錯誤詳情 (開發模式下顯示) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-6 text-left">
                <h3 className="font-bold text-red-800 mb-2">{t('error.errorDetails')}</h3>
                <p className="text-sm text-red-700 font-mono break-words">
                  {this.state.error.message}
                </p>
                {this.state.error.stack && (
                  <details className="mt-2">
                    <summary className="text-xs text-red-600 cursor-pointer">{t('error.showStackTrace')}</summary>
                    <pre className="text-xs text-red-600 mt-1 whitespace-pre-wrap break-words">
                      {this.state.error.stack}
                    </pre>
                  </details>
                )}
              </div>
            )}
            
            <div className="flex flex-col gap-3">
              <button
                onClick={this.handleRetry}
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-3 rounded-full border-2 border-black transition-all duration-200 hover:scale-105 flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                {t('common.retry')}
              </button>
              
              <button
                onClick={this.handleReload}
                className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-3 rounded-full border-2 border-black transition-all duration-200 hover:scale-105"
              >
                {t('error.reloadPage')}
              </button>
              
              <button
                onClick={() => window.location.href = '/'}
                className="bg-gray-500 hover:bg-gray-600 text-white font-bold px-6 py-3 rounded-full border-2 border-black transition-all duration-200 hover:scale-105"
              >
                {t('error.backToHome')}
              </button>
            </div>
            
            {/* 提示用戶檢查控制台 */}
            <div className="mt-6 p-3 bg-yellow-100 border-2 border-yellow-400 rounded-xl">
              <p className="text-xs text-yellow-800 font-semibold">
                {t('error.techSupport')}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;